import { createContext, useContext, useSyncExternalStore } from "react";
import type { Mesure, RaisonDeRefus, Via } from "./contract";
import { livreActif } from "./livre";
import { envoyerMesure } from "./mesure";
import type { RefusSauvegarde } from "./moteur/sauvegarde";
import type { Livre, TypeFin } from "./moteur/types";
import {
  type Changements,
  codeDeSauvegarde,
  type Issue,
  jouerChoix,
  jouerReponse,
  lire,
  type MesureDePartie,
  type Partie,
  pageCourante,
  reprendre,
} from "./partie";
import {
  cleDePartie,
  ecrirePartie,
  lirePartie,
  type Rangement,
  rangementDuNavigateur,
} from "./rangement";

/**
 * Le magasin de la partie : il relie les regles (`partie.ts`), le stockage du navigateur
 * (`rangement.ts`) et les mesures (`mesure.ts`), et dit aux ecrans quoi montrer.
 *
 * La partie gardee fait foi, pas la memoire de l'onglet : chaque scan par l'appareil photo
 * ouvre un onglet de plus, et un onglet reste en arriere-plan peut avoir vu la partie avancer.
 * D'ou `recharger()` avant chaque action, et sur l'evenement `storage` du navigateur.
 */

/** Ce que l'ecran montre par-dessus la page : une question en cours, ou ce qui vient d'arriver. */
export type Attente =
  | {
      type: "confirmer";
      code: string;
      libelle: string;
      page: number;
      via: Via;
      /** Confirmer efface d'abord la partie en cours : le lecteur a repris le livre page 1. */
      recommencer: boolean;
    }
  | {
      type: "enigme";
      code: string;
      libelle: string;
      question: string;
      page: number;
      via: Via;
      recommencer: boolean;
    }
  | {
      type: "resultat";
      page: number;
      libelle: string;
      changements: Changements;
      fin: TypeFin | null;
    }
  | {
      type: "refus";
      code: string;
      raison: RaisonDeRefus;
      page: number;
      recommencer: boolean;
      via: Via;
    }
  | { type: "deja-fait"; libelle: string; page: number }
  | { type: "reprise"; page: number }
  | { type: "recommencee" };

export interface Vue {
  livre: Livre;
  partie: Partie | null;
  attente: Attente | null;
  /** La partie gardee ne se lisait plus (autre version du livre) : elle a ete ecartee. */
  illisible: boolean;
  /** Le navigateur refuse de garder la partie : elle ne vit que dans cet onglet. */
  indisponible: boolean;
}

export interface OptionsDuJeu {
  livre: Livre;
  rangement: Rangement;
  mesurer: (mesure: Mesure) => void;
  maintenant: () => number;
}

export class Jeu {
  private courante: Vue;
  private readonly abonnes = new Set<() => void>();

  constructor(private readonly options: OptionsDuJeu) {
    const lue = lirePartie(options.livre, options.rangement);
    if (lue.illisible) ecrirePartie(options.livre, options.rangement, null);
    this.courante = {
      livre: options.livre,
      partie: lue.partie,
      attente: null,
      illisible: lue.illisible,
      indisponible: lue.indisponible,
    };
  }

  /** L'instantane lu par les ecrans : un nouvel objet a chaque changement, jamais entre deux. */
  vue = (): Vue => this.courante;

  abonner = (abonne: () => void): (() => void) => {
    this.abonnes.add(abonne);
    return () => {
      this.abonnes.delete(abonne);
    };
  };

  private publier(changement: Partial<Vue>): void {
    this.courante = { ...this.courante, ...changement };
    for (const abonne of this.abonnes) abonne();
  }

  /**
   * Relit la partie gardee, qu'un autre onglet a pu faire avancer. Si la partie a change de
   * page ou de carnet, la question en cours parlait d'un etat depasse : elle est fermee, et
   * l'ecran montre la page ou en est vraiment la partie. Un simple compteur qui bouge (un refus
   * compte dans l'autre onglet) ne ferme rien.
   */
  recharger(): void {
    // Sans stockage, la copie de l'onglet est la seule qui existe.
    if (this.courante.indisponible) return;
    const lue = lirePartie(this.options.livre, this.options.rangement);
    if (lue.indisponible) {
      this.publier({ indisponible: true });
      return;
    }
    const avant = this.courante.partie;
    if (JSON.stringify(lue.partie) === JSON.stringify(avant)) return;
    const depassee =
      (lue.partie === null) !== (avant === null) ||
      JSON.stringify(lue.partie?.etat) !== JSON.stringify(avant?.etat);
    this.publier(depassee ? { partie: lue.partie, attente: null } : { partie: lue.partie });
  }

  private ranger(partie: Partie | null): void {
    const garde =
      !this.courante.indisponible &&
      ecrirePartie(this.options.livre, this.options.rangement, partie);
    this.publier({ partie, indisponible: !garde, illisible: false });
  }

  private mesurer(mesures: MesureDePartie[]): void {
    for (const mesure of mesures) {
      this.options.mesurer({ ...mesure, livre: this.options.livre.id } as Mesure);
    }
  }

  /**
   * Un code arrive : scanne dans l'appli, ouvert par l'appareil photo (`/c/<code>`) ou tape.
   * Rien n'est applique tant que le lecteur n'a pas confirme son choix, sauf sur une page qui
   * n'en propose qu'un : deux QR codes empiles se lisent facilement l'un pour l'autre.
   */
  recevoir(saisie: string, via: Via): void {
    this.recharger();
    const lecture = lire(this.options.livre, this.courante.partie, saisie);
    switch (lecture.type) {
      case "choix":
        if (lecture.seul) {
          this.appliquer(lecture.code, via);
          return;
        }
        this.publier({
          attente: {
            type: "confirmer",
            code: lecture.code,
            libelle: lecture.libelle,
            page: lecture.page,
            via,
            recommencer: false,
          },
        });
        return;
      case "enigme":
        this.publier({
          attente: {
            type: "enigme",
            code: lecture.code,
            libelle: lecture.libelle,
            question: lecture.question,
            page: lecture.page,
            via,
            recommencer: false,
          },
        });
        return;
      case "deja-fait":
        this.publier({
          attente: { type: "deja-fait", libelle: lecture.libelle, page: lecture.page },
        });
        return;
      case "refus":
        this.refuser(lecture.code, lecture.refus, lecture.page, lecture.recommencer, via);
        return;
    }
  }

  /** Le scanner a lu un QR code qui ne vient pas du livre. */
  qrEtranger(): void {
    this.recharger();
    const page = pageCourante(this.options.livre, this.courante.partie);
    this.refuser("", "pas-un-code", page, false, "scan");
  }

  private refuser(
    code: string,
    raison: RaisonDeRefus,
    page: number,
    recommencer: boolean,
    via: Via,
  ): void {
    const partie = this.courante.partie;
    // La scene ou en est le lecteur : c'est la que le livre l'a perdu.
    const scene = partie?.etat.scene ?? this.options.livre.depart;
    this.mesurer([{ quoi: "refus", raison, scene, via }]);
    if (partie) this.ranger({ ...partie, refus: partie.refus + 1 });
    this.publier({ attente: { type: "refus", code, raison, page, recommencer, via } });
  }

  /** Le lecteur confirme le choix scanne. */
  confirmer(): void {
    const attente = this.courante.attente;
    if (attente?.type !== "confirmer") return;
    // La partie en cours ne s'efface qu'ici, une fois le nouveau depart confirme.
    if (attente.recommencer) this.ranger(null);
    this.appliquer(attente.code, attente.via);
  }

  private appliquer(code: string, via: Via): void {
    this.recharger();
    const issue = jouerChoix(
      this.options.livre,
      this.courante.partie,
      code,
      via,
      this.options.maintenant(),
    );
    this.conclure(issue, code, via);
  }

  /** Le lecteur repond a l'enigme scannee. */
  repondre(reponse: string): void {
    const attente = this.courante.attente;
    if (attente?.type !== "enigme") return;
    if (attente.recommencer) this.ranger(null);
    else this.recharger();
    const issue = jouerReponse(
      this.options.livre,
      this.courante.partie,
      attente.code,
      reponse,
      attente.via,
      this.options.maintenant(),
    );
    this.conclure(issue, attente.code, attente.via);
  }

  /** Renoncer a une enigme vaut une mauvaise reponse, comme dans la table des chemins. */
  renoncer(): void {
    this.repondre("");
  }

  private conclure(issue: Issue, code: string, via: Via): void {
    // Refuse ici seulement si un autre onglet a fait avancer la partie entre-temps.
    if (!issue.ok) {
      this.refuser(code, issue.refus, issue.page, false, via);
      return;
    }
    const { coup } = issue;
    this.ranger(coup.partie);
    this.mesurer(coup.mesures);
    this.publier({
      attente: {
        type: "resultat",
        page: coup.page,
        libelle: coup.libelle,
        changements: coup.changements,
        fin: coup.fin,
      },
    });
  }

  /** Ferme la question en cours (un choix scanne par erreur, une enigme laissee pour plus tard). */
  fermer(): void {
    if (this.courante.attente) this.publier({ attente: null });
  }

  recommencer(): void {
    this.ranger(null);
    this.publier({ attente: { type: "recommencee" } });
  }

  /**
   * Le lecteur a repris le livre a la page 1 sans le dire au telephone : son code y est valable.
   * Le choix se confirme toujours, meme seul sur sa page, parce que le confirmer efface la partie
   * en cours ; rien n'est efface avant.
   */
  recommencerAvec(code: string, via: Via): void {
    this.recharger();
    const lecture = lire(this.options.livre, null, code);
    if (lecture.type === "choix") {
      this.publier({
        attente: {
          type: "confirmer",
          code: lecture.code,
          libelle: lecture.libelle,
          page: lecture.page,
          via,
          recommencer: true,
        },
      });
    } else if (lecture.type === "enigme") {
      this.publier({
        attente: {
          type: "enigme",
          code: lecture.code,
          libelle: lecture.libelle,
          question: lecture.question,
          page: lecture.page,
          via,
          recommencer: true,
        },
      });
    } else {
      this.fermer();
    }
  }

  /** Le code de sauvegarde, a afficher : aucune mesure, a appeler autant de fois qu'il faut. */
  codeActuel(): string | null {
    return codeDeSauvegarde(this.options.livre, this.courante.partie);
  }

  /** Le lecteur demande son code de sauvegarde : c'est mesure, une fois par demande. */
  demanderSauvegarde(): string | null {
    // Un autre onglet a pu faire avancer la partie : le code doit etre celui d'aujourd'hui.
    this.recharger();
    const code = this.codeActuel();
    if (code) this.mesurer([{ quoi: "sauvegarde" }]);
    return code;
  }

  reprendre(saisie: string): { ok: true; page: number } | { ok: false; refus: RefusSauvegarde } {
    const reprise = reprendre(this.options.livre, saisie, this.options.maintenant());
    if (!reprise.ok) {
      this.mesurer([{ quoi: "reprise", ok: false, raison: reprise.refus }]);
      return reprise;
    }
    this.ranger(reprise.partie);
    this.mesurer([{ quoi: "reprise", ok: true }]);
    this.publier({ attente: { type: "reprise", page: reprise.page } });
    return { ok: true, page: reprise.page };
  }
}

let parDefaut: Jeu | null = null;
let ecouteAutresOnglets = false;

/**
 * Le jeu de l'appli : le livre charge (`livre.ts`), le stockage du navigateur, les mesures vers
 * le backend. Il se cree au premier appel, une fois le livre charge : les routes attendent ce
 * chargement avant d'afficher l'accueil ou de lire un code (`routes.tsx`).
 */
export function jeuParDefaut(): Jeu {
  if (!parDefaut) {
    parDefaut = new Jeu({
      livre: livreActif(),
      rangement: rangementDuNavigateur(),
      mesurer: envoyerMesure,
      maintenant: () => Date.now(),
    });
  }
  if (!ecouteAutresOnglets && typeof window !== "undefined") {
    ecouteAutresOnglets = true;
    // Un autre onglet a joue (scan par l'appareil photo) : celui-ci se remet a jour. Seule la
    // partie du livre en jeu compte : chaque livre range la sienne sous sa propre cle.
    window.addEventListener("storage", (evenement) => {
      const jeu = parDefaut;
      if (!jeu) return;
      if (evenement.key === null || evenement.key === cleDePartie(jeu.vue().livre)) {
        jeu.recharger();
      }
    });
  }
  return parDefaut;
}

/** Pour les tests : remplace le jeu par defaut, ou l'oublie (`null`). */
export function remplacerJeuParDefaut(jeu: Jeu | null): void {
  parDefaut = jeu;
}

/** Un ecran peut recevoir un autre jeu que celui par defaut (les tests le font). */
export const JeuContext = createContext<Jeu | null>(null);

export function useJeu(): { vue: Vue; jeu: Jeu } {
  const jeu = useContext(JeuContext) ?? jeuParDefaut();
  const vue = useSyncExternalStore(jeu.abonner, jeu.vue, jeu.vue);
  return { vue, jeu };
}
